import { useCallback, useEffect, useRef, useState } from 'react';

interface IProps {
  action: () => void;
  // Called on the press, before the blur it causes reaches the field being edited.
  onPress?: () => void;
  disabled: boolean;
}

// A press blurs the edited field, whose recalculation disables the button before the click lands.
// A click meanwhile runs the action once the recalculation is done.
export const useDeferredPressAction = ({ action, onPress, disabled }: IProps) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isActionPending, setIsActionPending] = useState(false);
  const releaseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Deferred past the click, which a synchronous release would disable first.
  const onRelease = useCallback(() => {
    releaseTimeoutRef.current = setTimeout(() => setIsPressed(false));
  }, []);

  const onMouseDown = useCallback(() => {
    setIsPressed(true);
    onPress?.();
    document.addEventListener('mouseup', onRelease, { once: true });
  }, [onPress, onRelease]);

  const onClick = useCallback(() => {
    setIsPressed(false);

    if (disabled) {
      setIsActionPending(true);

      return;
    }

    action();
  }, [action, disabled]);

  useEffect(() => {
    if (!isActionPending || disabled) {
      return;
    }

    setIsActionPending(false);
    action();
  }, [action, disabled, isActionPending]);

  useEffect(
    () => () => {
      document.removeEventListener('mouseup', onRelease);

      if (releaseTimeoutRef.current) {
        clearTimeout(releaseTimeoutRef.current);
      }
    },
    [onRelease]
  );

  return { onMouseDown, onClick, isDisabled: disabled && !isPressed };
};
